import React from 'react';
import Skeleton from 'react-loading-skeleton';
import styled, { css } from 'styled-components';
import { lineHeight, theme, toRem } from 'design';
import { Appearance, Size } from 'design/types';
import { DisbursementMethod, PayoutStatus } from 'types';
import { capitalize } from 'utils/strings';
import { Tag } from '../Tag';

const StatusContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  width: auto;

  > * {
    &:first-child {
      margin-right: ${toRem(theme.space.sm)};
    }
  }
`;

interface IStatusProps {
  appearance: Appearance;
  label: string;
  subStatusLabel?: string;
  className?: string;
  isLoading?: boolean;
  size?: Size.LARGE | Size.SMALL;
}

const DEFAULT_COLOR = theme.color.success[50];

const Layout = styled.div<Partial<IStatusProps>>`
  flex-grow: 0;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  width: auto;
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.regular};
  font-size: ${toRem(theme.font.size.sm)};
  line-height: ${lineHeight(theme.font.size.sm)};
  color: inherit;

  .tag {
    margin-left: ${toRem(theme.space.sm)};
    font-size: ${toRem(theme.font.size.xs)};
    line-height: ${lineHeight(theme.font.size.xs)};
  }

  ${({ size }) =>
    size === 'large' &&
    css`
      @media (min-width: ${theme.breakpoint.med}px) {
        font-size: ${toRem(theme.font.size.lg)};
        line-height: ${lineHeight(theme.font.size.lg)};
      }
    `}

  ${({ isLoading }) =>
    isLoading &&
    css`
      width: 100%;
    `}

  > span {
    display: flex;

    &:last-child {
      flex-grow: 1;
    }
  }
`;

const INDICATOR_SIZE = {
  small: 7,
  large: 11,
};
const INDICATOR_MARGIN = {
  small: theme.space.sm,
  large: theme.space.sm,
};

const Indicator = styled.div.attrs({ 'aria-hidden': true, 'data-cy': 'status-indicator' })<Partial<IStatusProps>>`
  position: relative;
  display: flex;
  flex-grow: 0;
  flex-shrink: 0;
  width: ${toRem(INDICATOR_SIZE.small)};
  height: ${toRem(INDICATOR_SIZE.small)};
  margin-right: ${toRem(INDICATOR_MARGIN.small)};
  border-radius: 50%;
  background-color: ${DEFAULT_COLOR};

  ${({ size }) =>
    size === 'large' &&
    css`
      margin-left: ${toRem(INDICATOR_MARGIN.small / -2)};

      @media (min-width: ${theme.breakpoint.med}px) {
        width: ${toRem(INDICATOR_SIZE.large)};
        height: ${toRem(INDICATOR_SIZE.large)};
        margin-right: ${toRem(INDICATOR_MARGIN.large)};
        margin-left: ${toRem(INDICATOR_MARGIN.large / -2)};
      }
    `}

  ${({ appearance }) =>
    appearance === Appearance.CASH &&
    css`
      background-color: ${theme.color.cash[50]};
    `}

  ${({ appearance }) =>
    appearance === Appearance.PAYROLL &&
    css`
      background-color: ${theme.color.secondary[400]};
    `}

  ${({ appearance }) =>
    appearance === Appearance.PRIMARY &&
    css`
      background-color: ${theme.color.primary[500]};
    `}

  ${({ appearance }) =>
    appearance === Appearance.WARNING &&
    css`
      background-color: ${theme.color.warning[50]};
    `}

  ${({ appearance }) =>
    appearance === Appearance.DANGER &&
    css`
      background-color: ${theme.color.danger[50]};
    `}

  ${({ appearance }) =>
    appearance === Appearance.WHITE &&
    css`
      border: ${toRem(1.5)} solid ${theme.color.neutral[500]};
      background-color: ${theme.color.white};
    `}

  ${({ appearance }) =>
    appearance === Appearance.NEUTRAL &&
    css`
      background-color: ${theme.color.neutral[400]};
    `}

  ${({ appearance }) =>
    appearance === Appearance.SUCCESS &&
    css`
      background-color: ${theme.color.success[50]};
    `}
`;

const StatusLabel = styled.div``;

const LoadingLabel = styled(Skeleton)`
  && {
    width: 100%;
  }
`;

const LoadingIndicator = styled(Skeleton).attrs({ circle: true })<Partial<IStatusProps>>`
  && {
    width: ${toRem(INDICATOR_SIZE.small)};
    height: ${toRem(INDICATOR_SIZE.small)};
    margin-right: ${toRem(INDICATOR_MARGIN.small)};

    ${({ size }) =>
      size === 'large' &&
      css`
        @media (min-width: ${theme.breakpoint.med}px) {
          width: ${toRem(INDICATOR_SIZE.large)};
          height: ${toRem(INDICATOR_SIZE.large)};
          margin-right: ${toRem(INDICATOR_MARGIN.large)};
        }
      `}
  }
`;

export const Status: React.FC<IStatusProps> = ({ label, subStatusLabel, appearance = Appearance.SUCCESS, className, isLoading, size = Size.SMALL, ...rest }) => (
  <Layout className={className} size={size} isLoading={isLoading} {...rest}>
    {isLoading ? (
      <>
        <LoadingIndicator size={size} />
        <LoadingLabel />
      </>
    ) : (
      <>
        <Indicator size={size} appearance={appearance} />
        <StatusLabel>{label}</StatusLabel>
        {subStatusLabel && <Tag className="tag" data-cy="substatus-label" size={Size.SMALL} appearance={Appearance.WARNING} label={subStatusLabel} />}
      </>
    )}
  </Layout>
);

export const getStatusColor = (status: PayoutStatus | DisbursementMethod): Appearance => {
  switch (status) {
    case PayoutStatus.PAID:
    case DisbursementMethod.INSTANT:
      return Appearance.SUCCESS;
    case PayoutStatus.PENDING:
      return Appearance.WARNING;
    case PayoutStatus.EXPIRED:
      return Appearance.DANGER;
    case PayoutStatus.CASH:
      return Appearance.CASH;
    case PayoutStatus.PAYROLL:
      return Appearance.PAYROLL;
    case PayoutStatus.CANCELED:
      return Appearance.NEUTRAL;
    default:
      return Appearance.NEUTRAL;
  }
};

interface IStatusPaymentProps {
  className?: string;
  isLoading?: boolean;
  size?: Size.LARGE | Size.SMALL;
  status: PayoutStatus | DisbursementMethod;
  subStatusLabel?: string;
  subStatusAppearance?: Appearance;
  subStatusTooltip?: string;
  paymentId?: string;
}

export const StatusPayment = ({ className, isLoading, size = Size.SMALL, status, subStatusLabel, subStatusAppearance, subStatusTooltip, paymentId, ...rest }: IStatusPaymentProps) => (
  <StatusContainer className={className}>
    <Status appearance={getStatusColor(status)} label={capitalize(status)} size={size} isLoading={isLoading} {...rest} />
    {!isLoading && subStatusLabel && <Tag dataCy="substatus-label" size={Size.SMALL} appearance={subStatusAppearance || getStatusColor(status)} label={subStatusLabel} tooltipMessage={subStatusTooltip} tooltipId={`alert_${paymentId}`} />}
  </StatusContainer>
);
